const DISMISSED_AT_KEY = "frb-newsletter-exit-dismissed-at";
const SUBSCRIBED_KEY = "frb-newsletter-subscribed";
const SHOWN_KEY = "frb-newsletter-exit-shown";
const DISMISSAL_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

function storageAvailable() {
  if (typeof window === "undefined") return false;
  try {
    return Boolean(window.localStorage);
  } catch {
    return false;
  }
}

function sessionStorageAvailable() {
  if (typeof window === "undefined") return false;
  try {
    return Boolean(window.sessionStorage);
  } catch {
    return false;
  }
}

/**
 * The prompt is meant to appear once per browser session, but the component
 * remounts on every Inertia navigation, so an in-memory ref cannot carry the
 * claim across pages. Session storage can.
 */
export function newsletterPromptShownThisSession() {
  if (!sessionStorageAvailable()) return false;
  try {
    return window.sessionStorage.getItem(SHOWN_KEY) === "true";
  } catch {
    return false;
  }
}

export function rememberNewsletterPromptShown() {
  if (!sessionStorageAvailable()) return;
  try {
    window.sessionStorage.setItem(SHOWN_KEY, "true");
  } catch {
    /* A blocked storage API must not make the page unusable. */
  }
}

export function newsletterPromptSuppressed(now = Date.now()) {
  if (newsletterPromptShownThisSession()) return true;
  if (!storageAvailable()) return false;

  try {
    if (window.localStorage.getItem(SUBSCRIBED_KEY) === "true") return true;
    const dismissedAt = Number(window.localStorage.getItem(DISMISSED_AT_KEY));
    return Number.isFinite(dismissedAt) && dismissedAt > 0 && now - dismissedAt < DISMISSAL_WINDOW_MS;
  } catch {
    return false;
  }
}

export function rememberNewsletterDismissal() {
  if (!storageAvailable()) return;
  try {
    window.localStorage.setItem(DISMISSED_AT_KEY, String(Date.now()));
  } catch {
    /* A blocked storage API must not make the page unusable. */
  }
}

export function rememberNewsletterSubscription() {
  if (!storageAvailable()) return;
  try {
    window.localStorage.setItem(SUBSCRIBED_KEY, "true");
  } catch {
    /* The signup still succeeded even when storage is unavailable. */
  }
}

export type NewsletterConfirmation = {
  status: "subscribed" | "already_subscribed";
  email: string;
  source: string;
};

/**
 * The server sets `flash.newsletter` only after the address is stored. Reading
 * it from the page (not from local component state) is what lets the
 * confirmation survive a full page reload, e.g. Inertia's 409 hard refresh
 * when a tab was opened before a deploy.
 */
export function newsletterConfirmationFrom(flash: unknown, source: string): NewsletterConfirmation | null {
  if (!flash || typeof flash !== "object") return null;
  const value = (flash as Record<string, unknown>).newsletter;
  if (!value || typeof value !== "object") return null;
  const { status, email, source: from } = value as Record<string, unknown>;
  if (from !== source || typeof email !== "string") return null;
  if (status !== "subscribed" && status !== "already_subscribed") return null;
  return { status, email, source: from };
}

export function newsletterConfirmationCopy(confirmation: NewsletterConfirmation) {
  return confirmation.status === "already_subscribed"
    ? { heading: "You're already on the list.", body: `${confirmation.email} is saved for occasional shop updates.` }
    : { heading: "You're on the list.", body: `We saved ${confirmation.email} for occasional shop updates.` };
}
